// Course-compatibility sets for the "Follow the category" priority
// mode in AIRecipe.
//
// When the user picks a tight-category course (bake / dessert / prep)
// AND priority="category", we filter the pantry rows fed into the
// Claude prompt so incompatible items (savory meats, full proteins
// on a bake, raw vegetables on a dessert) never become narrative
// pressure. Without this filter, Claude drafts savory skillets under
// the Baked Goods chip because the hot dog on the shelf is too
// tempting to leave behind.
//
// Sets are curated — we prefer a miss (compatible item dropped from
// the palette because it's not on the list) over a false positive
// (savory meat leaking into a bake). Add ids here when you notice
// gaps; don't sub in a regex vibe check.
//
// BOTH the client's prompt builder AND the edge function need to
// agree on these sets. Edge fn copies the list into its own module
// (Supabase edge functions can't currently import from src/); keep
// them in sync when editing.

// ── BAKE ──────────────────────────────────────────────────────────────
// Flour-sugar-butter-eggs-leavening + aromatics + fruit + chocolate/nuts.
// Explicitly no meat, fish, prepared deli items, or strong-savory vegetables.
// Zucchini / carrot / pumpkin / banana are here because quick-bread and
// muffin applications keep them in-frame.
export const BAKE_COMPATIBLE_CANONICALS = new Set([
  // Flours
  "flour", "all_purpose_flour", "bread_flour", "cake_flour",
  "pastry_flour", "whole_wheat_flour", "almond_flour", "oat_flour",
  "rye_flour", "cornmeal", "semolina",
  // Sugars / sweeteners
  "sugar", "white_sugar", "brown_sugar", "light_brown_sugar",
  "dark_brown_sugar", "powdered_sugar", "confectioners_sugar",
  "honey", "maple_syrup", "molasses", "corn_syrup", "agave",
  // Fats
  "butter", "unsalted_butter", "salted_butter", "shortening", "lard",
  "oil_neutral", "vegetable_oil", "canola_oil", "olive_oil", "coconut_oil",
  // Dairy
  "eggs", "egg_whites", "egg_yolks", "milk", "whole_milk", "skim_milk",
  "buttermilk", "heavy_cream", "half_and_half", "sour_cream",
  "cream_cheese", "mascarpone", "ricotta", "yogurt", "greek_yogurt",
  // Leavening + core bake chemistry
  "yeast", "active_dry_yeast", "instant_yeast", "baking_powder",
  "baking_soda", "cream_of_tartar", "salt", "kosher_salt", "sea_salt",
  "vanilla_extract", "vanilla_bean", "almond_extract",
  // Chocolate / cocoa
  "chocolate", "chocolate_chips", "dark_chocolate", "milk_chocolate",
  "white_chocolate", "semisweet_chocolate", "cocoa", "cocoa_powder",
  // Nuts + seeds
  "walnuts", "pecans", "almonds", "hazelnuts", "peanuts", "peanut_butter",
  "cashews", "pistachios", "poppy_seeds", "sesame_seeds", "chia_seeds",
  "flax_seeds", "sunflower_seeds", "pumpkin_seeds",
  // Grains + oats
  "oats", "rolled_oats", "steel_cut_oats", "quick_oats",
  // Spices + aromatics for sweet bakes
  "cinnamon", "ground_cinnamon", "nutmeg", "cardamom", "ginger_ground",
  "cloves", "allspice", "star_anise", "black_pepper",
  // Citrus (zest / juice)
  "lemon", "lime", "orange", "lemon_juice", "lime_juice", "orange_juice",
  "lemon_zest", "orange_zest",
  // Fruit (fresh / dried / preserved)
  "banana", "apple", "pear", "berries", "blueberries", "raspberries",
  "strawberries", "blackberries", "cherries", "cranberries",
  "pumpkin", "pumpkin_puree", "zucchini", "carrot", "sweet_potato",
  "raisins", "dates", "figs", "prunes", "currants",
  "jam", "preserves", "marmalade", "fruit_preserves",
  // Coffee / tea for mochas, spice cakes, etc.
  "coffee", "instant_coffee", "espresso_powder",
]);

// ── DESSERT ──────────────────────────────────────────────────────────
// Superset of bake + frozen/custard/confection ingredients. A dessert
// doesn't need to be baked, so gelatin, mascarpone, liqueurs, and
// ice-cream-base dairy all belong here too.
export const DESSERT_COMPATIBLE_CANONICALS = new Set([
  ...BAKE_COMPATIBLE_CANONICALS,
  // Set / thickener
  "gelatin", "cornstarch", "tapioca_starch", "arrowroot", "agar",
  // Rich dairy for custards / ice creams / panna cotta
  "condensed_milk", "sweetened_condensed_milk", "evaporated_milk",
  // Liqueurs / alcohol used in desserts
  "rum", "brandy", "kahlua", "grand_marnier", "amaretto",
  // Confection staples
  "marshmallows", "caramel", "toffee", "praline",
]);

// ── PREP ─────────────────────────────────────────────────────────────
// Components / condiments only. Aromatics, vinegars, acids, herbs,
// spices, stocks' building blocks. No full proteins (stock bones
// are a special case — user will probably add them manually when
// they mean to make stock).
export const PREP_COMPATIBLE_CANONICALS = new Set([
  // Aromatics
  "onion", "yellow_onion", "red_onion", "shallot", "garlic", "ginger",
  "celery", "carrot", "leek", "scallion", "green_onion",
  // Acids / vinegars / citrus
  "vinegar", "white_vinegar", "apple_cider_vinegar", "red_wine_vinegar",
  "white_wine_vinegar", "rice_vinegar", "balsamic_vinegar",
  "lemon", "lime", "lemon_juice", "lime_juice",
  // Salt + sweeteners for balance
  "salt", "kosher_salt", "sea_salt", "sugar", "honey", "maple_syrup",
  // Umami / funk
  "soy_sauce", "fish_sauce", "worcestershire", "miso", "anchovy",
  "mustard", "dijon_mustard", "whole_grain_mustard",
  // Fats for confits / dressings
  "olive_oil", "neutral_oil", "sesame_oil", "butter",
  // Herbs (fresh + dried)
  "parsley", "cilantro", "basil", "thyme", "rosemary", "sage",
  "oregano", "tarragon", "dill", "chives", "bay_leaf",
  "dried_thyme", "dried_oregano", "dried_basil", "dried_parsley",
  // Whole spices + blends
  "black_pepper", "peppercorns", "white_pepper", "red_pepper_flakes",
  "cumin", "coriander", "paprika", "smoked_paprika", "turmeric",
  "fennel_seed", "mustard_seed", "celery_seed", "caraway",
  "cinnamon", "cloves", "allspice", "star_anise", "juniper",
  // Aromatics for pickles / ferments
  "garlic_cloves", "chili", "jalapeno", "serrano", "red_chili",
]);

// Resolve which set to use for a given course id. Null for any
// course that doesn't filter (main / side / appetizer / any).
export function compatibilitySetFor(course) {
  if (course === "bake")    return BAKE_COMPATIBLE_CANONICALS;
  if (course === "dessert") return DESSERT_COMPATIBLE_CANONICALS;
  if (course === "prep")    return PREP_COMPATIBLE_CANONICALS;
  return null;
}

// Filter a list of pantry rows down to items compatible with the
// given course, when priority is "category". Non-filtering courses
// (and non-category priority) pass through unchanged.
//
// Policy when a row's canonicalId isn't in the set:
//   - Drop. Better a smaller palette than a contaminated one.
//   - Exception: rows with no canonicalId at all (free-text) are
//     kept only when course is null/non-tight — no way to verify
//     compatibility of "homemade sauce" (the free-text name). If
//     that becomes a real annoyance we can add a keyword check,
//     but initially it's better to hide them than risk Claude
//     weaving them into a bake.
// Priority modes that pre-filter the pantry against course compat:
//   classic       — meal prompt is king, course constraint is hard;
//                   keep the pantry palette tight to category-fit rows
//                   so Claude doesn't draft around incompatible items.
//   chefs_choice  — pass through (chef has free hand to pull from
//                   anywhere in the pantry).
//   pantry        — pass through (the entire pantry IS the source list;
//                   filtering would hide options the user wants to see
//                   in pantry-strict mode).
export function filterPantryByCourse(rows, course, priority) {
  if (priority !== "classic") return rows;
  const set = compatibilitySetFor(course);
  if (!set) return rows;
  return (rows || []).filter(r => r?.canonicalId && set.has(r.canonicalId));
}

// True when filterPantryByCourse would actually remove rows for this
// (course, priority) combination. Lets the edge function tailor the
// PRECEDENCE block so it doesn't claim "the pantry has been filtered"
// when no filter ran (main / side / appetizer under classic priority
// historically tripped this — the prompt lied and Claude drafted
// around expiring items anyway).
export function courseHasPantryFilter(course, priority) {
  if (priority !== "classic") return false;
  return compatibilitySetFor(course) != null;
}

// ── COURSE-PALETTE SCORING ───────────────────────────────────────────
//
// Soft scoring (boost / demote) layered on top of the existing hard
// classic-priority filter. Used by `aiContext.buildAIContext` to
// rank-and-truncate the pantry to 40 items even in default / chefs-
// choice priority modes. Hub membership and category come straight
// from the canonical row; literal sets cover the tail (sweets,
// baking-only leaveners, savory pantry condiments) that aren't
// neatly hub-shaped.
//
// Scoring is intentionally soft, not a hard filter. A really bad
// fit gets a heavy negative score and rarely makes the cut, but
// can still surface for an outlier draft if the rest of the
// pantry is sparse.

// Sugars, chocolates, flavorings — boost in dessert/bake, demote
// in main/side/appetizer.
export const SWEETS_CANONICALS = new Set([
  "sugar", "white_sugar", "brown_sugar", "light_brown_sugar",
  "dark_brown_sugar", "powdered_sugar", "confectioners_sugar",
  "honey", "maple_syrup", "molasses", "corn_syrup", "agave",
  "chocolate", "chocolate_chips", "dark_chocolate", "milk_chocolate",
  "white_chocolate", "semisweet_chocolate", "cocoa", "cocoa_powder",
  "vanilla_extract", "vanilla", "vanilla_bean", "almond_extract",
  "marshmallows", "caramel", "toffee", "praline", "sprinkles",
  "frosting",
]);

// Items whose only credible role is sweet/baked-goods. Demoted hard
// in any savory course (instant yeast in pasta is the case the user
// flagged 2026-05-02).
export const BAKING_ONLY_CANONICALS = new Set([
  "yeast", "active_dry_yeast", "instant_yeast", "fresh_yeast",
  "baking_powder", "baking_soda", "cream_of_tartar",
  "vanilla_extract", "vanilla", "almond_extract",
  "sprinkles", "frosting", "fondant",
  "powdered_sugar", "confectioners_sugar",
  "chocolate_chips", "marshmallows",
  "cocoa_powder",
]);

// Pantry-category canonicals that anchor savory cooking. Used as a
// boost set for main/appetizer and a demote set for bake/dessert.
export const SAVORY_PANTRY_CANONICALS = new Set([
  "olive_oil", "vegetable_oil", "canola_oil", "neutral_oil",
  "sesame_oil", "avocado_oil", "peanut_oil",
  "vinegar", "white_vinegar", "apple_cider_vinegar",
  "red_wine_vinegar", "white_wine_vinegar", "rice_vinegar",
  "balsamic_vinegar",
  "soy_sauce", "fish_sauce", "worcestershire", "miso", "anchovy",
  "hot_sauce", "sriracha", "tabasco",
  "tomato_paste", "tomato_sauce", "canned_tomatoes",
  "tomato_puree", "marinara",
  "mustard", "dijon_mustard", "whole_grain_mustard", "yellow_mustard",
  "chicken_broth", "beef_broth", "vegetable_broth", "stock",
  "salt", "kosher_salt", "sea_salt",
  "black_pepper", "peppercorns", "white_pepper", "red_pepper_flakes",
  "cumin", "coriander", "paprika", "smoked_paprika", "turmeric",
  "fennel_seed", "mustard_seed", "celery_seed",
  "dried_thyme", "dried_oregano", "dried_basil", "dried_parsley",
  "bay_leaf",
  "garlic_powder", "onion_powder",
  "capers", "olives", "kalamata_olives", "green_olives",
]);

// Per-course boost rules. Returns the score added to a pantry item
// based on its canonicalId, the looked-up ingredient row (which
// carries `parentId` for hub membership and `category` for
// produce/meat/dairy), and the requested course id.
//
// Negative numbers are demotes — items unlikely to fit the course.
//
// `course` values used in practice today: "bake", "dessert", "prep",
// "main", "side", "appetizer", "any". Unknown values fall through
// to a small baking-only demote so e.g. yeast doesn't dominate
// freeform drafts either.
export function coursePaletteScore({ canonicalId, ingredient, course }) {
  if (!canonicalId) return 0;
  const hub = ingredient?.parentId || null;
  const category = ingredient?.category || null;

  switch (course) {
    case "bake": {
      let s = 0;
      if (hub === "flour_hub") s += 60;
      if (SWEETS_CANONICALS.has(canonicalId)) s += 60;
      if (category === "dairy") s += 40;
      if (canonicalId === "eggs" || canonicalId === "egg_whites" ||
          canonicalId === "egg_yolks") s += 40;
      if (BAKING_ONLY_CANONICALS.has(canonicalId)) s += 40; // leaveners are good here
      if (category === "meat") s -= 80;
      if (SAVORY_PANTRY_CANONICALS.has(canonicalId)) s -= 60;
      return s;
    }
    case "dessert": {
      let s = 0;
      if (hub === "flour_hub" || hub === "milk_hub" ||
          hub === "yogurt_hub") s += 60;
      if (SWEETS_CANONICALS.has(canonicalId)) s += 60;
      if (category === "dairy") s += 40;
      if (BAKING_ONLY_CANONICALS.has(canonicalId)) s += 30;
      if (category === "meat") s -= 80;
      if (SAVORY_PANTRY_CANONICALS.has(canonicalId)) s -= 60;
      return s;
    }
    case "side": {
      let s = 0;
      if (category === "produce") s += 60;
      if (hub === "bread_hub" || hub === "pasta_hub" ||
          hub === "rice_hub" || hub === "bean_hub") s += 60;
      if (BAKING_ONLY_CANONICALS.has(canonicalId)) s -= 80;
      if (SWEETS_CANONICALS.has(canonicalId)) s -= 60;
      return s;
    }
    case "main": {
      let s = 0;
      if (hub === "beef_hub" || hub === "chicken_hub" ||
          hub === "pork_hub" || hub === "turkey_hub" ||
          hub === "seafood_hub") s += 60;
      if (category === "produce") s += 40;
      if (hub === "pasta_hub" || hub === "rice_hub" ||
          hub === "bread_hub" || hub === "bean_hub") s += 40;
      if (SAVORY_PANTRY_CANONICALS.has(canonicalId)) s += 30;
      if (BAKING_ONLY_CANONICALS.has(canonicalId)) s -= 80;
      if (SWEETS_CANONICALS.has(canonicalId)) s -= 60;
      return s;
    }
    case "appetizer": {
      let s = 0;
      if (hub === "cheese_hub") s += 60;
      if (category === "produce") s += 50;
      if (hub === "bread_hub") s += 50;
      if (SAVORY_PANTRY_CANONICALS.has(canonicalId)) s += 20;
      if (BAKING_ONLY_CANONICALS.has(canonicalId)) s -= 80;
      if (SWEETS_CANONICALS.has(canonicalId)) s -= 60;
      return s;
    }
    case "prep": {
      // PREP_COMPATIBLE_CANONICALS is the existing tight set —
      // anything inside it is fair game for stocks/condiments/ferments.
      // Outside the set isn't demoted because prep is small-scope by
      // nature; user expectations are narrower.
      return PREP_COMPATIBLE_CANONICALS.has(canonicalId) ? 60 : 0;
    }
    case "any":
    default: {
      // Mild demote for baking-only items — yeast leaking into a
      // freeform "what should I cook" prompt is still wrong.
      if (BAKING_ONLY_CANONICALS.has(canonicalId)) return -40;
      return 0;
    }
  }
}
